import { useEffect, useMemo, useRef } from 'react';
import { Pane, Polyline } from 'react-leaflet';
import { LatLngTuple, Polyline as LeafletPolyline } from 'leaflet';

type LeafletPolylineWithPath = LeafletPolyline & { _path?: SVGElement };

const DEFAULT_SWEEP_DURATION_MS = 4000;
const DEFAULT_LEAD_SPREAD = 5;
const DEFAULT_TRAIL_SPREAD = 1.8;
const DEFAULT_WEIGHT = 5;
const COLOR_STEPS = 64;

const buildColorTable = (baseColor: string, sweepColor: string): string[] => {
  const r1 = Number.parseInt(baseColor.slice(1, 3), 16);
  const g1 = Number.parseInt(baseColor.slice(3, 5), 16);
  const b1 = Number.parseInt(baseColor.slice(5, 7), 16);
  const r2 = Number.parseInt(sweepColor.slice(1, 3), 16);
  const g2 = Number.parseInt(sweepColor.slice(3, 5), 16);
  const b2 = Number.parseInt(sweepColor.slice(5, 7), 16);
  const table = new Array<string>(COLOR_STEPS + 1);
  for (let i = 0; i <= COLOR_STEPS; i += 1) {
    const t = i / COLOR_STEPS;
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    table[i] =
      `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
  return table;
};

const downsamplePositions = (positions: LatLngTuple[], maxPoints: number): LatLngTuple[] => {
  if (positions.length <= maxPoints) return positions;
  const step = (positions.length - 1) / (maxPoints - 1);
  const sampled: LatLngTuple[] = [];
  for (let i = 0; i < maxPoints - 1; i += 1) {
    sampled.push(positions[Math.round(i * step)]);
  }
  sampled.push(positions[positions.length - 1]);
  return sampled;
};

const interpolatePositions = (positions: LatLngTuple[], minPoints: number): LatLngTuple[] => {
  if (positions.length >= minPoints || positions.length < 2) return positions;
  const gaps = positions.length - 1;
  const insertionsPerGap = Math.ceil((minPoints - positions.length) / gaps);
  const result: LatLngTuple[] = [];
  for (let i = 0; i < gaps; i += 1) {
    const [lat1, lng1] = positions[i];
    const [lat2, lng2] = positions[i + 1];
    result.push(positions[i]);
    for (let j = 1; j <= insertionsPerGap; j += 1) {
      const t = j / (insertionsPerGap + 1);
      result.push([lat1 + (lat2 - lat1) * t, lng1 + (lng2 - lng1) * t]);
    }
  }
  result.push(positions[positions.length - 1]);
  return result;
};

type SweepingPolylineProps = {
  positions: LatLngTuple[];
  baseColor: string;
  sweepColor: string;
  weight?: number;
  sweepDurationMs?: number;
  leadSpread?: number;
  trailSpread?: number;
  maxSegments?: number;
  minSegments?: number;
  paneName?: string;
  paneStyle?: React.CSSProperties;
};

export const SweepingPolyline = ({
  positions,
  baseColor,
  sweepColor,
  weight = DEFAULT_WEIGHT,
  sweepDurationMs = DEFAULT_SWEEP_DURATION_MS,
  leadSpread = DEFAULT_LEAD_SPREAD,
  trailSpread = DEFAULT_TRAIL_SPREAD,
  maxSegments,
  minSegments,
  paneName,
  paneStyle,
}: SweepingPolylineProps) => {
  const sampledPositions = useMemo(() => {
    let next = positions;
    if (minSegments && next.length < minSegments + 2) {
      next = interpolatePositions(next, minSegments + 2);
    }
    if (maxSegments && next.length > maxSegments + 2) {
      next = downsamplePositions(next, maxSegments + 2);
    }
    return next;
  }, [positions, maxSegments, minSegments]);

  const segments = useMemo(() => {
    if (sampledPositions.length < 3) return [];
    const numSegments = sampledPositions.length - 2;
    return Array.from({ length: numSegments }, (_, i) => ({
      positions: [sampledPositions[i], sampledPositions[i + 1], sampledPositions[i + 2]] as LatLngTuple[],
      segmentPosition: i / Math.max(numSegments - 1, 1),
    }));
  }, [sampledPositions]);

  const basePathOptions = useMemo(() => ({ color: baseColor, weight }), [baseColor, weight]);

  const refs = useRef<(LeafletPolyline | null)[]>([]);

  const refCallbacks = useMemo(
    () =>
      segments.map((_, i) => (el: LeafletPolyline | null) => {
        refs.current[i] = el;
      }),
    [segments],
  );

  useEffect(() => {
    if (segments.length === 0) return undefined;

    const colorTable = buildColorTable(baseColor, sweepColor);
    const entryMargin = 1 / leadSpread;
    const exitMargin = 1 / trailSpread;
    const range = 1 + entryMargin + exitMargin;
    let animId: number;

    const animate = (timestamp: number) => {
      const progress = (timestamp % sweepDurationMs) / sweepDurationMs;
      const phase = -entryMargin + progress * range;

      for (let i = 0; i < segments.length; i += 1) {
        const polyline = refs.current[i] as LeafletPolylineWithPath | null;
        // eslint-disable-next-line no-underscore-dangle
        const path = polyline?._path;
        if (path) {
          const diff = segments[i].segmentPosition - phase;
          const spread = diff > 0 ? leadSpread : trailSpread;
          const falloff = Math.min(Math.abs(diff) * spread, 1);
          const brightness = (Math.cos(falloff * Math.PI) + 1) / 2;
          const bucket = Math.min(COLOR_STEPS, Math.floor(brightness * COLOR_STEPS));
          path.setAttribute('stroke', colorTable[bucket]);
        }
      }

      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [segments, baseColor, sweepColor, sweepDurationMs, leadSpread, trailSpread]);

  const polylines = useMemo(
    () =>
      segments.map((segment, i) => (
        <Polyline
          key={`sweep-${segment.segmentPosition}-${segment.positions[0][0]}-${segment.positions[0][1]}`}
          ref={refCallbacks[i]}
          positions={segment.positions}
          pathOptions={basePathOptions}
        />
      )),
    [segments, refCallbacks, basePathOptions],
  );

  if (segments.length === 0) return null;

  if (paneName) {
    return (
      <Pane name={paneName} style={paneStyle}>
        {polylines}
      </Pane>
    );
  }

  return polylines;
};
